import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { formatDateOnly } from "@mediatechgroup/calendar-date";
import { useSelector, useDispatch } from "react-redux";
import { selectMembershipDetails, setMembership } from "../../store/slices/membershipSlice";
import { getScheduledPayments, cancelScheduledPayment } from "../../api/paymentsAPI";
import { getMemberById } from "../../api/membershipAPI";
import { useTheme } from "../../context/ThemeContext";
import {
  getCardBackground,
  getPrimaryText,
  getSecondaryText,
  getMutedText,
  getPageBackground,
} from "../../utils/themeUtils";
import { selectIsAdmin, selectIsStaff } from "../../store/slices/authSlice";
import { useTenantFormat } from "../../hooks/useTenantFormat";
import { toast } from "react-toastify";
import ConfirmDeleteModal from "../common/ConfirmDeleteModal";
import ScheduledPaymentTooltip from "./ScheduledPaymentTooltip";
import BillingPager from "./BillingHistory/BillingPager";

interface MyComponentProps {
  refreshApiCalls: number;
  setRefreshApiCalls: Dispatch<SetStateAction<number>>;
}

const MemberScheduledPayments: React.FC<MyComponentProps> = ({
  refreshApiCalls,
  setRefreshApiCalls,
}) => {
  const { isDarkMode } = useTheme();
  const { formatCurrency } = useTenantFormat();
  const dispatch = useDispatch();
  const membership: any = useSelector(selectMembershipDetails);
  const [payments, setPayments] = useState<any[]>([]);
  const [selectedPayment, setSelectedPayment] = useState<any | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);

  // Loading states
  const [fetchLoading, setFetchLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(5);

  // Tooltip state
  const [tooltipData, setTooltipData] = useState<null | {
    x: number;
    y: number;
    payment: any;
  }>(null);

  const token = localStorage.getItem("authToken");
  const isAdmin = useSelector(selectIsAdmin);
  const isStaff = useSelector(selectIsStaff);

  const fetchScheduledPayments = async () => {
    setFetchLoading(true);
    try {
      // Fetch all scheduled payments
      const res = await getScheduledPayments(token, membership?.id);
      if (res?.success) {
        // Filter out cancelled and completed payments. A FAILED payment stays
        // listed but is marked as failed and listed after the ones that will
        // still run: it used to look exactly like an upcoming payment.
        const activePayments = (res?.data || []).filter(
          (payment: any) => payment.status !== 'cancelled' && payment.status !== 'completed'
        );
        const willRun = activePayments.filter((p: any) => p.status !== 'failed');
        const failed = activePayments.filter((p: any) => p.status === 'failed');
        setPayments([...willRun, ...failed]);
      } else {
        // No scheduled payments or error - just set empty array
        setPayments([]);
      }
    } catch (err) {
      // Axios interceptor handles Sentry capture
      setPayments([]);
    } finally {
      setLoading(false);
      setFetchLoading(false);
    }
  };

  const fetchMembershipDetails = async () => {
    try {
      const response = await getMemberById(token, membership.id);

      // Update membership details in Redux store
      if (response && response.data) {
        dispatch(setMembership(response?.data));
      }
    } catch (err) {
      // Axios interceptor handles Sentry capture
    }
  };

  const handleCancelClick = (payment: any) => {
    setSelectedPayment(payment);
    setShowCancelModal(true);
  };

  const handleConfirmCancel = async () => {
    if (!selectedPayment) return;

    setCancelling(true);
    try {
      const res = await cancelScheduledPayment(token, selectedPayment.id);
      if (res?.success) {
        toast.success("Scheduled payment canceled successfully");
        fetchScheduledPayments();
        fetchMembershipDetails();
        setRefreshApiCalls(prev => prev + 1);
      } else {
        toast.error(res?.message || "Failed to cancel payment");
      }
    } catch (err) {
      // Axios interceptor handles Sentry capture
      toast.error("Error cancelling scheduled payment");
    } finally {
      setShowCancelModal(false);
      setSelectedPayment(null);
      setCancelling(false);
    }
  };

  const handleCancelDelete = () => {
    setSelectedPayment(null);
    setShowCancelModal(false);
  };


  useEffect(() => {
    if (membership?.id) {
      fetchScheduledPayments();
    }
  }, [membership?.id, refreshApiCalls]);

  // Get current items for pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  // Current items according to pagination
  const currentItems = payments?.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  if (loading) {
    return (
      <div className="w-full flex justify-center items-center py-8">
        <div className="flex justify-center items-center">
          <div className="w-6 h-6 border-2 border-gray-400 dark:border-gray-500 border-t-transparent rounded-full animate-spin"></div>
          <span className="ml-2 text-gray-500 dark:text-gray-400">
            Loading scheduled payments...
          </span>
        </div>
      </div>
    );
  }

  // Hide section if no scheduled payments
  const hasPayments = payments?.length > 0;

  if (!hasPayments) {
    return null; // Don't render anything if no scheduled payments
  }

  return (
    <div className={`mt-6 rounded-lg ${getCardBackground(isDarkMode)}`}>
      {/* No side padding: lines up with the Billing History and Future
          Invoices tables in the same container. */}
      <h2
        className={`text-2xl font-bold flex-shrink-0 mb-3 ${getPrimaryText(
          isDarkMode
        )}`}
      >
        Scheduled Payments
      </h2>
      {/* Same header row as Future Invoices. An empty "legend" box used to
          sit here; its bottom margin put 16px more space between this
          heading and its table than the other billing tables have. */}
      <div className="w-full flex justify-end">
        {/* Small loader for fetch actions */}
        {fetchLoading && (
          <div
            className={`flex items-center space-x-4 mb-4 ${getMutedText(
              isDarkMode
            )}`}
          >
            <div
              className={`w-4 h-4 border-2 ${
                isDarkMode ? "border-gray-800" : "border-gray-400"
              } border-t-transparent rounded-full animate-spin`}
            ></div>
            <span className="text-sm">Updating results...</span>
          </div>
        )}
      </div>
      <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
        <table
          className={`w-full text-sm text-left rtl:text-right ${getMutedText(
            isDarkMode
          )}`}
        >
          <thead
            className={`text-xs ${getSecondaryText(
              isDarkMode
            )} uppercase ${getPageBackground(isDarkMode)}`}
          >
            <tr>
              <th scope="col" className="px-6 py-3">
                Payment Method
              </th>
              <th scope="col" className="px-6 py-3">
                Amount
              </th>
              <th scope="col" className="px-6 py-3">
                Scheduled Date
              </th>
              <th scope="col" className="px-6 py-3">
                Description
              </th>
              <th scope="col" className="px-6 py-3">
                Status
              </th>
            </tr>
          </thead>

          <tbody>
            {currentItems?.map((payment) => (
              <tr
                key={payment.id}
                data-status={payment.status}
                className={`border-b cursor-pointer hover:opacity-80 transition-opacity ${
                  payment.status === "failed"
                    ? isDarkMode
                      ? "bg-red-950 border-gray-700"
                      : "bg-red-50 border-gray-200"
                    : isDarkMode
                    ? "bg-yellow-900 border-gray-700"
                    : "bg-yellow-50 border-gray-200"
                }`}
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  setTooltipData({
                    x: rect.left + rect.width / 2,
                    y: rect.bottom + window.scrollY,
                    payment,
                  });
                }}
              >
                {/* Payment Method */}
                <td className={`px-6 py-4 ${getPrimaryText(isDarkMode)}`}>
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                      isDarkMode
                        ? "bg-blue-900 text-blue-300"
                        : "bg-blue-100 text-blue-800"
                    }`}
                  >
                    Card
                  </span>
                </td>

                {/* Amount */}
                <td
                  className={`px-6 py-4 font-semibold ${getPrimaryText(
                    isDarkMode
                  )}`}
                >
                  {formatCurrency(payment.amount || 0)}
                </td>

                {/* Scheduled Date */}
                <td className={`px-6 py-4 ${getPrimaryText(isDarkMode)}`}>
                  {formatDateOnly(payment.scheduled_date)}
                </td>

                {/* Description */}
                <td className={`px-6 py-4 ${getSecondaryText(isDarkMode)}`}>
                  {payment.description || payment.note || "Scheduled payment"}
                </td>

                {/* Status: a failed payment will not run again by itself and
                    is not counted in "Upcoming". */}
                <td className={`px-6 py-4 ${getPrimaryText(isDarkMode)}`}>
                  {payment.status === "failed" ? (
                    <span className={isDarkMode ? "text-red-300" : "text-red-700"} title={payment.error_message || undefined}>
                      Failed{payment.error_message ? `: ${payment.error_message}` : ""}
                    </span>
                  ) : payment.status === "processing" ? (
                    "Being charged"
                  ) : Number(payment.retry_count) > 0 ? (
                    "Will try again"
                  ) : (
                    "Scheduled"
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination — one line, same control and spacing as Billing History
          and Future Invoices. */}
      <BillingPager
        idPrefix="scheduled-payments"
        className="mt-3"
        total={payments?.length || 0}
        page={currentPage}
        pageSize={itemsPerPage}
        onPageChange={handlePageChange}
        itemLabel="scheduled payments"
        itemLabelSingular="scheduled payment"
        previousLabel="Earlier scheduled payments"
        nextLabel="Later scheduled payments"
      />

      {/* Cancel Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={showCancelModal}
        title="Cancel Scheduled Payment"
        message={`Are you sure you want to cancel this scheduled payment of ${formatCurrency(selectedPayment?.amount || 0)} on ${selectedPayment?.scheduled_date ? formatDateOnly(selectedPayment.scheduled_date) : ''}?`}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmCancel}
        confirmText="Cancel Payment"
        cancelText="Keep Payment"
        loading={cancelling}
      />

      {/* Scheduled Payment Tooltip */}
      {tooltipData && (
        <>
          {/* Backdrop to catch clicks outside */}
          <div
            className="fixed inset-0 z-40 bg-black bg-opacity-20"
            onClick={() => setTooltipData(null)}
          />
          {/* Tooltip */}
          <ScheduledPaymentTooltip
            payment={tooltipData.payment}
            onClose={() => setTooltipData(null)}
            onCancel={(payment) => {
              setTooltipData(null);
              handleCancelClick(payment);
            }}
            isAdminOrStaff={isAdmin || isStaff}
          />
        </>
      )}
    </div>
  );
};

export default MemberScheduledPayments;
